/**
 * @file useSpaceSummary.js
 * @description Space summary dialog functionality
 *
 * Extracted from UnifiedBrickSchemaManager.vue
 * Handles:
 * - Space information display
 * - Airflow supply information
 * - Temperature sensor information
 * - Equipment summary
 */

import { useQuasar } from 'quasar';

export function useSpaceSummary() {
  const $q = useQuasar();

  /**
   * Show space summary dialog
   * @param {Object} node - Space node
   * @param {Object} options - Options for getting space data
   * @param {Function} options.getSpaceAirflowSupply - Function to get airflow supply
   * @param {Function} options.getSpaceTemperatureSensors - Function to get temperature sensors
   * @param {Object} options.spaceAirflowData - Space airflow data
   * @param {Array} options.treeNodes - Tree nodes
   */
  const showSpaceSummary = (node, options) => {
    const { getSpaceAirflowSupply, getSpaceTemperatureSensors, spaceAirflowData, treeNodes } = options;

    if (node.type !== 'space') {return;}

    try {
      const equipment = node.children?.filter((child) => child.type === 'equipment') || [];

      // Extract space ID properly
      let spaceId = node.data?.id || node.id;
      if (typeof spaceId === 'string' && spaceId.includes('space-')) {
        spaceId = parseInt(spaceId.replace('space-', ''));
      } else if (typeof spaceId === 'string') {
        spaceId = parseInt(spaceId);
      }

      // Build the HTML content for the dialog
      let htmlContent = '<div class="q-pa-md">';

      // Get airflow supply and temperature sensor information with error handling
      let airflowSupply = null;
      let tempSensors = [];

      try {
        airflowSupply = getSpaceAirflowSupply(node, {
          spaceAirflowData: spaceAirflowData,
          treeNodes: treeNodes,
        });
      } catch (error) {
        console.error('Failed to get space airflow supply:', error);
      }

      try {
        tempSensors = getSpaceTemperatureSensors(node, {
          treeNodes: treeNodes,
        });
      } catch (error) {
        console.error('Failed to get space temperature sensors:', error);
      }

      // For demonstration, add mock data for Conference Rooms if no real data exists
      let mockAirflowSupply = null;
      if (!airflowSupply && (spaceId === 173 || spaceId === 174)) {
        if (spaceId === 173) {
          mockAirflowSupply = 'AHU-1 > Zone 1, Zone 3 (Demo Data)';
        } else if (spaceId === 174) {
          mockAirflowSupply = 'AHU-1 > Zone 2 (Demo Data)';
        }
      }

      const displayAirflowSupply = airflowSupply || mockAirflowSupply;

      // Add Space Information section
      htmlContent += '<div class="q-mb-lg">';
      htmlContent += '<strong>Space Information</strong><br/>';
      htmlContent += '<div class="q-mt-sm text-body2">';
      htmlContent += `Name: ${node.label}<br/>`;
      htmlContent += `ID: ${spaceId}<br/>`;
      if (node.data?.building_level_name) {
        htmlContent += `Level: ${node.data.building_level_name}<br/>`;
      }
      htmlContent += '</div>';
      htmlContent += '</div>';

      // Add Airflow Supply section
      htmlContent += '<div class="q-mb-lg">';
      if (displayAirflowSupply) {
        htmlContent += '<strong>Airflow Supply:</strong><br/>';
        htmlContent += `<div class="q-mt-sm text-body2">${displayAirflowSupply}</div>`;
        if (mockAirflowSupply) {
          htmlContent += '<div class="text-orange q-mt-xs text-caption">Note: This is demonstration data. Configure actual airflow connections in the Airflow tab.</div>';
        }
      } else {
        htmlContent += '<strong>Airflow Status:</strong><br/>';
        htmlContent += '<div class="text-warning q-mt-sm">Space not connected to airflow system</div>';
        htmlContent += '<div class="text-caption q-mt-xs">No VAV or terminal unit found. This space may not have proper ventilation.</div>';
      }
      htmlContent += '</div>';

      // Add Temperature Monitoring section if sensors exist
      if (tempSensors.length > 0) {
        htmlContent += '<div class="q-mb-lg">';
        htmlContent += '<strong>Temperature Monitoring:</strong><br/>';
        htmlContent += '<div class="q-mt-sm text-body2">';
        tempSensors.forEach((sensor) => {
          htmlContent += `- ${sensor.name} (located in ${sensor.location})<br/>`;
        });
        htmlContent += '</div>';
        htmlContent += '</div>';
      }

      // Add Equipment section (simplified - table moved to sidebar)
      htmlContent += '<div class="q-mb-md">';
      if (equipment.length > 0) {
        htmlContent += '<strong>Equipment:</strong><br/>';
        htmlContent += `<div class="text-subtitle2 q-mt-sm">${equipment.length} equipment item(s) in this space</div>`;
        htmlContent += '<div class="text-caption q-mt-xs">View detailed documentation status in the sidebar when this space is selected.</div>';
      } else {
        htmlContent += '<strong>Equipment:</strong><br/>';
        htmlContent += '<div class="text-subtitle2 q-mt-sm">No equipment in this space</div>';
        htmlContent += '<div class="text-caption q-mt-xs">Add equipment to this space to track documentation.</div>';
      }
      htmlContent += '</div>';

      // Show the dialog with proper content
      $q.dialog({
        title: `Extra Information: ${node.label}`,
        message: htmlContent,
        html: true,
        ok: {
          label: 'Close',
          color: 'primary',
        },
        style: 'min-width: 600px; max-width: 800px;',
      });
    } catch (error) {
      // Show error dialog
      $q.dialog({
        title: 'Error',
        message: 'Failed to load space summary. Please check the console for details.',
        ok: {
          label: 'OK',
          color: 'negative',
        },
      });
    }
  };

  /**
   * Navigate to airflow tab and open documentation dialog
   * @param {Object} node - Equipment node
   * @param {Object} documentationEquipment - Ref for documentation equipment
   * @param {Object} documentationDialog - Ref for documentation dialog visibility
   */
  const navigateToAirflow = (node, documentationEquipment, documentationDialog) => {
    documentationEquipment.value = {
      id: node.data?.id,
      type: 'AHU',
      name: node.label,
      brickClass: node.brickClass || node.data?.brickClass,
    };
    documentationDialog.value = true;
  };

  return {
    showSpaceSummary,
    navigateToAirflow,
  };
}
